/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Button, Segment } from "semantic-ui-react";
import { Checkbox, FormControlLabel, TextField, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { changeBrowserTabTitle } from "../slices/browserTabTitle/browserTabTitleSlice";
import { globalLoadingOff, globalLoadingOn, selectGlobalLoading } from "../slices/globalLoading/globalLoadingSlice";
import { selectCurrentUserApiToken, selectCurrentUserName } from "../slices/currentUser/currentUserSlice";
import { changeTool } from "../slices/footer/footerSlice";
import { selectSearchSettings } from "../slices/searchSettings/searchSettingsSlice";
import submitNctid, { submitNctidsFromSearch } from "../functions/submitNctidAPI";
import SaveSearchModal from "./SaveSearchModal";
import AlertModal from "./AlertModal";
import { changeFoiFocus } from "../slices/foiOfFocus/foiOfFocusSlice";
import submitFhirJson from "../functions/submitFhirJsonAPI";

const EmptyBoxCharacter = () => {
    return <span className="box emptyBox">☐&nbsp;</span>;
}

const ctGovSearch = async (searchString, searchRecruitingState, apiToken, dispatch, setCtGovSearchClicked, setCtGovSearchResultsState, setAlertModalState, setCtGovSearchRequestUrlState) => {
    if (apiToken) {
        dispatch(globalLoadingOn());
        setCtGovSearchClicked(false);
        try {

            let aggFilters = searchRecruitingState ? ["status%3Arec"] : undefined;
            let response = await submitNctidsFromSearch({ "term": searchString }, aggFilters, apiToken);
            if (response?.success) {
                //alert("Processing " + response.numberOfNctids + " records... It will be a while to be finished.");
                if (response.searchResults?.length) {
                    if (response.searchResults.length > 19999) {
                        setAlertModalState(prevState => {
                            return {
                                ...prevState,
                                "open": true,
                                "header": "> 20,000 results",
                                "content": "The search exceeded 20,000 results. Only the first 20,000 are shown here."
                            };
                        });
                    }
                    setCtGovSearchResultsState(response.searchResults);
                    setCtGovSearchRequestUrlState(response.url);
                } else {
                    setAlertModalState(prevState => {
                        return {
                            ...prevState,
                            "open": true,
                            "header": "No search results",
                            "content": ""
                        };
                    });
                }
            } else {
                setAlertModalState(prevState => {
                    return {
                        ...prevState,
                        "open": true,
                        "header": "SUBMISSION FAILED",
                        "content": ""
                    };
                });
            }
        } catch (e) { console.log(e); }
        setCtGovSearchClicked(true);
        dispatch(globalLoadingOff());
    } else {
        setAlertModalState(prevState => {
            return {
                ...prevState,
                "open": true,
                "header": "Please login",
                "content": "Log into FEvIR to use this function."
            };
        });
    }
}

const advancedCtGovSearch = async (searchData, setCtGovSearchResultsState, setCtGovSearchClicked, setAlertModalState, apiToken, dispatch, setCtGovSearchRequestUrlState) => {
    const {
        condition,
        otherTerms,
        intervention,
        location,
        studyStatus,
        studyResultsType,
        sex,
        acceptsHealthyVolunteers,
        phase0,
        phase1,
        phase2,
        phase3,
        phase4,
        phaseNA,
        studyTypeInt,
        studyTypeObs,
        studyTypeObsPatReg,
        studyTypeExp,
        studyTypeExpIndiv,
        studyTypeExpInter,
        studyTypeExpTreat,
        documentsProtocols,
        documentsSAPs,
        documentsICFs,
        funderNIH,
        funderFed,
        funderIndustry,
        funderOther,
        titles,
        outcomeMeasure,
        sponsor,
        leadSponsor,
        studyIds,
        FDAAAViolations
    } = searchData;
    if (apiToken) {
        dispatch(globalLoadingOn());
        setCtGovSearchClicked(false);
        try {

            let aggFilters = [];
            if (sex) {
                if (sex === "f") {
                    aggFilters.push("sex:f");
                } else if (sex === "m") {
                    aggFilters.push("sex:m");
                }
                //If the value is "all" then nothing is pushed
            }
            if (studyResultsType) {
                if (studyResultsType === "with") {
                    aggFilters.push("results:with");
                } else if (studyResultsType === "without") {
                    aggFilters.push("results:without");
                }
                //If the value is "all" then nothing is pushed
            }
            if (acceptsHealthyVolunteers) {
                aggFilters.push("healthy:y");
            }
            if (phase0 || phase1 || phase2 || phase3 || phase4 || phaseNA) {
                let phaseParameter = "phase:";
                if (phase0) {
                    phaseParameter += "0%20";
                }
                if (phase1) {
                    phaseParameter += "1%20";
                }
                if (phase2) {
                    phaseParameter += "2%20";
                }
                if (phase3) {
                    phaseParameter += "3%20";
                }
                if (phase4) {
                    phaseParameter += "4%20";
                }
                if (phaseNA) {
                    phaseParameter += "NA%20";
                }
                phaseParameter = phaseParameter.substring(0, phaseParameter.length - 3); //Removes the last three characters
                aggFilters.push(phaseParameter);
            }
            if (studyTypeInt || studyTypeObs || studyTypeObsPatReg || studyTypeExp || studyTypeExpIndiv || studyTypeExpInter || studyTypeExpTreat) {
                let studyTypeParameter = "studyType:";
                if (studyTypeInt) {
                    studyTypeParameter += "int%20";
                }
                if (studyTypeObs) {
                    studyTypeParameter += "obs%20";
                }
                if (studyTypeObsPatReg) {
                    studyTypeParameter += "obs_patreg%20";
                }
                if (studyTypeExp) {
                    studyTypeParameter += "exp%20";
                }
                if (studyTypeExpIndiv) {
                    studyTypeParameter += "exp_indiv%20";
                }
                if (studyTypeExpInter) {
                    studyTypeParameter += "exp_inter%20";
                }
                if (studyTypeExpTreat) {
                    studyTypeParameter += "exp_treat%20";
                }
                studyTypeParameter = studyTypeParameter.substring(0, studyTypeParameter.length - 3); //Removes the last three characters
                aggFilters.push(studyTypeParameter);
            }
            if (documentsProtocols || documentsSAPs || documentsICFs) {
                let documentsParameter = "docs:";
                if (documentsProtocols) {
                    documentsParameter += "prot%20";
                }
                if (documentsSAPs) {
                    documentsParameter += "sap%20";
                }
                if (documentsICFs) {
                    documentsParameter += "icf%20";
                }
                documentsParameter = documentsParameter.substring(0, documentsParameter.length - 3); //Removes the last three characters
                aggFilters.push(documentsParameter);
            }
            if (funderNIH || funderFed || funderIndustry || funderOther) {
                let funderType = "funderType:";
                if (funderNIH) {
                    funderType += "nih%20";
                }
                if (funderFed) {
                    funderType += "fed%20";
                }
                if (funderIndustry) {
                    funderType += "industry%20";
                }
                if (funderOther) {
                    funderType += "other%20";
                }
                funderType = funderType.substring(0, funderType.length - 3); //Removes the last three characters
                aggFilters.push(funderType);
            }
            if (FDAAAViolations) {
                aggFilters.push("violation:y");
            }
            if (aggFilters.length === 0) {
                aggFilters = undefined;
            }
            let querySearchTypes = {
                "cond": condition,
                "term": otherTerms,
                "intr": intervention,
                "locn": location, //Confused if this should be locnStr and HOW this is different from Facility Name
                "titles": titles,
                "outc": outcomeMeasure,
                "spons": sponsor,
                "lead": leadSponsor,
                "id": studyIds,
                ///"??? what should this be ???": facility, //how is this different from location? API parameter-wise
                //"patient": "", //	See "PatientSearch Area" on Search Areas for more details. https://clinicaltrials.gov/data-api/about-api/search-areas#PatientSearch
            };
            let filterOverallStatus = studyStatus === "recruitingAndNotYet" ? ["NOT_YET_RECRUITING", "RECRUITING"] : undefined;
            let response = await submitNctidsFromSearch(querySearchTypes, aggFilters, apiToken, filterOverallStatus);
            if (response?.success) {
                //alert("Processing " + response.numberOfNctids + " records... It will be a while to be finished.");
                if (response.searchResults?.length) {
                    if (response.searchResults.length > 19999) {
                        setAlertModalState(prevState => {
                            return {
                                ...prevState,
                                "open": true,
                                "header": "> 20,000 results",
                                "content": "The search exceeded 20,000 results. Only the first 20,000 are shown here."
                            };
                        });
                    }
                    setCtGovSearchResultsState(response.searchResults);
                    setCtGovSearchRequestUrlState(response.url);
                } else {
                    setAlertModalState(prevState => {
                        return {
                            ...prevState,
                            "open": true,
                            "header": "No search results",
                            "content": ""
                        };
                    });
                }
            } else {
                setAlertModalState(prevState => {
                    return {
                        ...prevState,
                        "open": true,
                        "header": "SUBMISSION FAILED",
                        "content": ""
                    };
                });
            }
        } catch (e) { console.log(e); }
        setCtGovSearchClicked(true);
        dispatch(globalLoadingOff());
    } else {
        setAlertModalState(prevState => {
            return {
                ...prevState,
                "open": true,
                "header": "Please login",
                "content": "Log into FEvIR to use this function."
            };
        });
    }
}

const submitNCTId = async (nctid, redirect, CTgovEntryState, changeCTgovEntryState, setAlertModalState, apiToken, navigate, dispatch, setCtGovSearchClicked, searchRecruitingState, setCtGovSearchResultsState, setCtGovSearchRequestUrlState) => {
    if (nctid === undefined) {
        nctid = CTgovEntryState.nctid;
    }
    if (redirect === undefined) {
        redirect = true;
    }
    nctid = nctid.replaceAll(" ", "").replaceAll("\n", "");
    let nctIdList;
    if (nctid) {
        if (nctid.includes(",")) {
            nctIdList = [];
            for (let entry of nctid.split(",")) {
                if (entry && entry.length === 11) {
                    nctIdList.push(entry);
                }
            }
        }
    } else {
        setAlertModalState(prevState => {
            return {
                ...prevState,
                "open": true,
                "header": "Please enter a NCT ID.",
                "content": ""
            };
        });
    }

    if (nctIdList?.length === 1) {
        nctid = nctIdList[0];
        nctIdList = undefined
    }

    if (nctIdList) {
        //Returns a list of titles for a list of NCT IDs
        //NCT06927973,NCT00408044
        ctGovSearch(nctIdList.join(","), searchRecruitingState, apiToken, dispatch, setCtGovSearchClicked, setCtGovSearchResultsState, setAlertModalState, setCtGovSearchRequestUrlState);
    } else {
        //Process as single NCTID entry
        changeCTgovEntryState(true, "loadingSubmission");
        let response = await submitNctid(nctid, apiToken);

        if (response?.success) {
            if (redirect) {
                if (response.jobAlreadyRunning && response.message) {
                    setAlertModalState(prevState => {
                        return {
                            ...prevState,
                            "open": true,
                            "header": "Job Currently In Process",
                            "content": response.message
                        };
                    });
                } else if (response.numberOfResourcesCreated) {
                    if (response.largeCollection) {
                        const messageContent = "The ClinicalTrials.gov record was converted to " + response.numberOfResourcesCreated + " FHIR Resources. Click out to view the ResearchStudy Resource. Because the number of Resources created is so large, it may take a few minutes before the referenced Resources are loaded to the FEvIR Platform.";
                        setAlertModalState(prevState => {
                            return {
                                ...prevState,
                                "open": true,
                                "header": "Processing",
                                "content": messageContent
                            };
                        });
                    } else {
                        const messageContent = "The ClinicalTrials.gov record was converted to " + response.numberOfResourcesCreated + " FHIR Resources. Click out to view the ResearchStudy Resource.";
                        setAlertModalState(prevState => {
                            return {
                                ...prevState,
                                "open": true,
                                "header": "Complete",
                                "content": messageContent
                            };
                        });
                    }
                } else if (response.message) {
                    setAlertModalState(prevState => {
                        return {
                            ...prevState,
                            "open": true,
                            "header": "",
                            "content": response.message
                        };
                    });
                }
                if (response.researchstudyfoi) {
                    //dispatch(changeFoiFocus(response.researchstudyfoi));
                    navigate(`/resources/ResearchStudy/${response.researchstudyfoi}`);
                }
            }
        } else if (response?.errormessage) {
            changeCTgovEntryState(false, "loadingSubmission");
            setAlertModalState(prevState => {
                return {
                    ...prevState,
                    "open": true,
                    "header": "Error",
                    "content": response.errormessage
                };
            });
        } else {
            changeCTgovEntryState(false, "loadingSubmission");
            const messageContent = "We were unable to convert this citation. Perhaps this NCT ID: " + nctid + " doesn't exist or there are issues in its structure.";
            setAlertModalState(prevState => {
                return {
                    ...prevState,
                    "open": true,
                    "header": "Error",
                    "content": messageContent
                };
            });
        }
    }
};

// eslint-disable-next-line no-empty-pattern
const CTgovConvertPage = ({ }) => {
    const [searchParams] = useSearchParams();
    const urlNCTid = searchParams.get("nctid");
    const dispatch = useDispatch();
    const siteIsLoading = useSelector(selectGlobalLoading);
    const navigate = useNavigate();
    const location = useLocation();
    const apiToken = useSelector(selectCurrentUserApiToken);
    const userName = useSelector(selectCurrentUserName);
    const searchSettings = useSelector(selectSearchSettings);
    const [openSaveSearchModalState, setOpenSaveSearchModalState] = useState(false);

    let nctIdFill = "";
    if (location.hash?.length === 12) {
        nctIdFill = location.hash.substring(1);
    }

    const [CTgovEntryState, setCTgovEntryState] = useState({
        "nctid": urlNCTid || nctIdFill, "loadingSubmission": false
    });

    const [ctGovSearchState, setCtGovSearchState] = useState("");
    const [searchRecruitingState, setSearchRecruitingState] = useState(false);
    const [ctGovSearchResultsState, setCtGovSearchResultsState] = useState([]);
    const [ctGovSearchRequestUrlState, setCtGovSearchRequestUrlState] = useState("");
    const [ctGovSearchClicked, setCtGovSearchClicked] = useState(false);
    //const [loading, setLoading] = useState(false);
    const [openAdvancedCTgovSearchState, setOpenAdvancedCTgovSearchState] = useState(false);

    const [searchCondition, setSearchCondition] = useState("");
    const [searchOtherTerms, setSearchOtherTerms] = useState("");
    const [searchIntervention, setSearchIntervention] = useState("");
    const [searchLocation, setSearchLocation] = useState("");

    const [searchTitles, setSearchTitles] = useState("");
    const [searchOutcomeMeasure, setSearchOutcomeMeasure] = useState("");
    const [searchSponsor, setSearchSponsor] = useState("");
    const [searchLeadSponsor, setSearchLeadSponsor] = useState("");
    const [searchStudyIds, setSearchStudyIds] = useState("");
    const [searchFDAAAViolations, setSearchFDAAAViolations] = useState(false);

    const [searchStudyStatus, setSearchStudyStatus] = useState("all");
    const [searchSex, setSearchSex] = useState("all");
    const [searchStudyResultsType, setSearchStudyResultsType] = useState("all");
    const [searchAcceptsHealthyVolunteers, setSearchAcceptsHealthyVolunteers] = useState(false);
    const [searchPhase0, setSearchPhase0] = useState(false);
    const [searchPhase1, setSearchPhase1] = useState(false);
    const [searchPhase2, setSearchPhase2] = useState(false);
    const [searchPhase3, setSearchPhase3] = useState(false);
    const [searchPhase4, setSearchPhase4] = useState(false);
    const [searchPhaseNA, setSearchPhaseNA] = useState(false);
    const [searchStudyTypeInt, setSearchStudyTypeInt] = useState(false);
    const [searchStudyTypeObs, setSearchStudyTypeObs] = useState(false);
    const [searchStudyTypeObsPatReg, setSearchStudyTypeObsPatReg] = useState(false);
    const [searchStudyTypeExp, setSearchStudyTypeExp] = useState(false);
    const [searchStudyTypeExpIndiv, setSearchStudyTypeExpIndiv] = useState(false);
    const [searchStudyTypeExpInter, setSearchStudyTypeExpInter] = useState(false);
    const [searchStudyTypeExpTreat, setSearchStudyTypeExpTreat] = useState(false);
    const [searchDocumentsProtocols, setSearchDocumentsProtocols] = useState(false);
    const [searchDocumentsSAPs, setSearchDocumentsSAPs] = useState(false);
    const [searchDocumentsICFs, setSearchDocumentsICFs] = useState(false);

    const [searchFunderNIH, setSearchFunderNIH] = useState(false);
    const [searchFunderFed, setSearchFunderFed] = useState(false);
    const [searchFunderIndustry, setSearchFunderIndustry] = useState(false);
    const [searchFunderOther, setSearchFunderOther] = useState(false);

    const [saveSearchResultsClickedState, setSaveSearchResultsClickedState] = useState(false);
    const [alertModalState, setAlertModalState] = useState({
        open: false, alertHistory: [], header: "", content: ""
    });

    const changeCTgovEntryState = (newValue, field) => {
        setCTgovEntryState(prevState => {
            return {
                ...prevState,
                [field]: newValue
            };
        });
    };

    const handleKeyUp = (event) => {
        if (event.key === 'Enter' && !alertModalState.open) {
            submitNCTId(undefined, undefined, CTgovEntryState, changeCTgovEntryState, setAlertModalState, apiToken, navigate, dispatch, setCtGovSearchClicked, searchRecruitingState, setCtGovSearchResultsState, setCtGovSearchRequestUrlState);
        }
    }

    const collectSearchParameters = () => {
        return {
            searchRecruiting: searchRecruitingState,
            condition: searchCondition,
            otherTerms: searchOtherTerms,
            intervention: searchIntervention,
            location: searchLocation,
            studyStatus: searchStudyStatus,
            studyResultsType: searchStudyResultsType,
            sex: searchSex,
            acceptsHealthyVolunteers: searchAcceptsHealthyVolunteers,
            phase0: searchPhase0,
            phase1: searchPhase1,
            phase2: searchPhase2,
            phase3: searchPhase3,
            phase4: searchPhase4,
            phaseNA: searchPhaseNA,
            studyTypeInt: searchStudyTypeInt,
            studyTypeObs: searchStudyTypeObs,
            studyTypeObsPatReg: searchStudyTypeObsPatReg,
            studyTypeExp: searchStudyTypeExp,
            studyTypeExpIndiv: searchStudyTypeExpIndiv,
            studyTypeExpInter: searchStudyTypeExpInter,
            studyTypeExpTreat: searchStudyTypeExpTreat,
            documentsProtocols: searchDocumentsProtocols,
            documentsSAPs: searchDocumentsSAPs,
            documentsICFs: searchDocumentsICFs,
            funderNIH: searchFunderNIH,
            funderFed: searchFunderFed,
            funderIndustry: searchFunderIndustry,
            funderOther: searchFunderOther,
            titles: searchTitles,
            outcomeMeasure: searchOutcomeMeasure,
            sponsor: searchSponsor,
            leadSponsor: searchLeadSponsor,
            studyIds: searchStudyIds,
            FDAAAViolations: searchFDAAAViolations
        }
    }

    const submitLibraryResource = async (fhirJson, title) => {
        let response = await submitFhirJson(fhirJson, title, apiToken);
        let resourceFOI;
        if (response?.success) {
            resourceFOI = response.formstateid;
        } else {
            setAlertModalState(prevState => {
                return {
                    ...prevState,
                    "open": true,
                    "header": "Error",
                    "content": "Problem creating Library of resulting Citation Resources."
                };
            });
            return null;
        }
        dispatch(changeFoiFocus(resourceFOI));
        navigate(`/resources/Library/${resourceFOI}`);
    }

    useEffect(() => {
        dispatch(changeBrowserTabTitle("FEvIR ClinicalTrials.gov-to-FEvIR Converter"));
        dispatch(changeTool("Computable Publishing®: ClinicalTrials.gov-to-FEvIR Converter"));
    }, []);

    useEffect(() => {
        if ((urlNCTid || nctIdFill) && apiToken) {
            submitNCTId(urlNCTid || nctIdFill, undefined, CTgovEntryState, changeCTgovEntryState, setAlertModalState, apiToken, navigate, dispatch, setCtGovSearchClicked, searchRecruitingState, setCtGovSearchResultsState, setCtGovSearchRequestUrlState);
        }
    }, [apiToken]);

    useEffect(() => {
        if (searchSettings.source === "ctgov") { //searchSettings?.savedSearch &&
            let searchQueryString = searchSettings.searchstring;
            if (searchSettings.searchparameters?.updatedSearchStartDate) {
                let earlydate = searchSettings.searchparameters.updatedSearchStartDate.replaceAll("/", "-");
                searchQueryString += " AND AREA[LastUpdatePostDate]RANGE[" + earlydate + ",MAX]";
            }
            setCtGovSearchState(searchQueryString);
            let searchData = {};
            let advancedSearch = false;
            if (searchSettings.searchparameters) {
                setSearchRecruitingState(searchSettings.searchparameters.searchRecruiting ?? false);
                setSearchCondition(searchSettings.searchparameters.condition || "");
                setSearchOtherTerms(searchSettings.searchparameters.otherTerms || "");
                setSearchIntervention(searchSettings.searchparameters.intervention || "");
                setSearchLocation(searchSettings.searchparameters.location || "");
                setSearchStudyStatus(searchSettings.searchparameters.studyStatus || "all");
                setSearchStudyResultsType(searchSettings.searchparameters.studyResultsType || "all");
                setSearchSex(searchSettings.searchparameters.sex || "all");
                setSearchAcceptsHealthyVolunteers(searchSettings.searchparameters.acceptsHealthyVolunteers ?? false);
                setSearchPhase0(searchSettings.searchparameters.phase0 ?? false);
                setSearchPhase1(searchSettings.searchparameters.phase1 ?? false);
                setSearchPhase2(searchSettings.searchparameters.phase2 ?? false);
                setSearchPhase3(searchSettings.searchparameters.phase3 ?? false);
                setSearchPhase4(searchSettings.searchparameters.phase4 ?? false);
                setSearchPhaseNA(searchSettings.searchparameters.phaseNA ?? false);
                setSearchStudyTypeInt(searchSettings.searchparameters.studyTypeInt ?? false);
                setSearchStudyTypeObs(searchSettings.searchparameters.studyTypeObs ?? false);
                setSearchStudyTypeObsPatReg(searchSettings.searchparameters.studyTypeObsPatReg ?? false);
                setSearchStudyTypeExp(searchSettings.searchparameters.studyTypeExp ?? false);
                setSearchStudyTypeExpIndiv(searchSettings.searchparameters.studyTypeExpIndiv ?? false);
                setSearchStudyTypeExpInter(searchSettings.searchparameters.studyTypeExpInter ?? false);
                setSearchStudyTypeExpTreat(searchSettings.searchparameters.studyTypeExpTreat ?? false);
                setSearchDocumentsProtocols(searchSettings.searchparameters.documentsProtocols ?? false);
                setSearchDocumentsSAPs(searchSettings.searchparameters.documentsSAPs ?? false);
                setSearchDocumentsICFs(searchSettings.searchparameters.documentsICFs ?? false);
                setSearchFunderNIH(searchSettings.searchparameters.funderNIH ?? false);
                setSearchFunderFed(searchSettings.searchparameters.funderFed ?? false);
                setSearchFunderIndustry(searchSettings.searchparameters.funderIndustry ?? false);
                setSearchFunderOther(searchSettings.searchparameters.funderOther ?? false);
                setSearchTitles(searchSettings.searchparameters.titles || "");
                setSearchOutcomeMeasure(searchSettings.searchparameters.outcomeMeasure || "");
                setSearchSponsor(searchSettings.searchparameters.sponsor || "");
                setSearchLeadSponsor(searchSettings.searchparameters.leadSponsor || "");
                setSearchStudyIds(searchSettings.searchparameters.studyIds || "");
                setSearchFDAAAViolations(searchSettings.searchparameters.FDAAAViolations ?? false);
                searchData = { ...searchSettings.searchparameters, ...searchData };
                for (let key in searchSettings.searchparameters) {
                    if (key !== "searchRecruiting" && key !== "updatedSearchStartDate" &&
                        key !== "updatedSearchEndDate") {
                        if (key === "studyStatus" || key === "studyResultsType" || key === "sex") {
                            if (searchSettings.searchparameters[key] !== "all") {
                                advancedSearch = true;
                                setOpenAdvancedCTgovSearchState(true);
                                break;
                            }
                        } else if (searchSettings.searchparameters[key]) {      //Meaning not an empty string and not false
                            advancedSearch = true;
                            setOpenAdvancedCTgovSearchState(true);
                            break;
                        }
                    }
                }
            }
            if (advancedSearch) {
                advancedCtGovSearch(searchData, setCtGovSearchResultsState, setCtGovSearchClicked, setAlertModalState, apiToken, dispatch, setCtGovSearchRequestUrlState);
            } else {
                if (searchSettings.searchstring) {
                    ctGovSearch(searchQueryString, searchSettings.searchRecruiting, apiToken, dispatch, setCtGovSearchClicked, setCtGovSearchResultsState, setAlertModalState, setCtGovSearchRequestUrlState);
                }
            }
        }
    }, [searchSettings]);

    useEffect(() => {
        if (saveSearchResultsClickedState === true) {
            let searchString = (ctGovSearchState + " " + searchOtherTerms).trim();
            let title = "ClinicalTrials.gov Search Results for " + searchString.substring(0, 40) + " for " + userName;
            let fhirJson = {
                "resourceType": "Library", "title": title, "status": "active",
                "type": {
                    "coding": [{
                        "system": "http://terminology.hl7.org/CodeSystem/library-type",
                        "code": "asset-collection", "display": "Asset Collection"
                    }]
                },
                "date": new Date().toISOString(),
                "description": "",
                "content": []
            };

            if (ctGovSearchResultsState?.length > 0) {
                fhirJson.description = "Your search includes " + ctGovSearchResultsState.length + " studies.";
                if (ctGovSearchResultsState.length >= 20000) {
                    fhirJson.description += " (Search results are not included after 20,000 entries.)";
                }

                for (const result of ctGovSearchResultsState) {
                    fhirJson.content.push({
                        "url": "https://clinicaltrials.gov/study/" + result.nctid,
                        "title": result.display
                    });
                }
            } else {
                fhirJson.description = "No studies were found on your search.";
            }
            fhirJson.description += " Your search string was: " + searchString + ". ";
            fhirJson.description += "The search request URL was: " + ctGovSearchRequestUrlState;
            submitLibraryResource(fhirJson, title);
        }
    }, [saveSearchResultsClickedState]);

    return (
        <div>
            <AlertModal alertModalState={alertModalState} setAlertModalState={setAlertModalState} />
            <SaveSearchModal openSaveSearchModalState={openSaveSearchModalState}
                setOpenSaveSearchModalState={setOpenSaveSearchModalState}
                searchParameters={collectSearchParameters()}
                source={"ctgov"} searchString={ctGovSearchState}
                header={"Label your ClinicalTrials.gov search"}
                searched={ctGovSearchRequestUrlState !== ""} />
            <Segment className={`containerSegment maxRemainderOfPageSegment`} raised>
                <br />
                <>
                    <TextField style={{ width: "180px" }} className="inputField" type='text' size="small"
                        variant='outlined' placeholder='NCT ID'
                        value={CTgovEntryState.nctid}
                        onKeyUp={handleKeyUp}
                        onChange={(e) => { changeCTgovEntryState(e.target.value, "nctid"); }}
                    />
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                    <span style={{display: 'inline-block', marginTop: "5px"}}>
                        <Button className="formButton positive"
                            content="Submit" compact
                            onClick={() => submitNCTId(undefined, undefined, CTgovEntryState, changeCTgovEntryState, setAlertModalState, apiToken, navigate, dispatch, setCtGovSearchClicked, searchRecruitingState, setCtGovSearchResultsState, setCtGovSearchRequestUrlState)}
                            disabled={CTgovEntryState.loadingSubmission} />
                    </span>
                    <br />
                    {CTgovEntryState.loadingSubmission &&
                        <img className={CTgovEntryState.loadingSubmission ? 'visible' : 'invisible'}
                            style={{ height: "22px" }} src="/spinner.gif" alt="Loading" />
                    }
                    <div style={{ "textAlign": "left", "width": "100%" }}>
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        <h3>
                            {
                                CTgovEntryState.loadingSubmission ?
                                    <>Your requested ClinicalTrials.gov study record is being converted to</>
                                    :
                                    <><br />Enter an NCT Identifier to automatically create</>
                            } HL7<sup>&#174;</sup> FHIR<sup>&#174;</sup> Resources.
                        </h3>
                        <span>HL7<sup>&#174;</sup> and FHIR<sup>&#174;</sup> are the registered trademarks of Health Level Seven International and their use of these trademarks does not constitute an endorsement by HL7.</span>
                    </div>
                </>
                <br /><br /><br />
                <div style={{ "textAlign": "left" }}>
                    <TextField style={{ width: "960px" }} className="inputField" size="small" variant='outlined'
                        placeholder='Search Terms' type='text'
                        value={ctGovSearchState} onKeyUp={(event) => {
                            if (event.key === 'Enter' && ctGovSearchState) {
                                ctGovSearch(ctGovSearchState, searchRecruitingState, apiToken, dispatch, setCtGovSearchClicked, setCtGovSearchResultsState, setAlertModalState, setCtGovSearchRequestUrlState);
                            }
                        }}
                        onChange={(e) => { setCtGovSearchState(e.target.value); }}
                    />
                    &nbsp;&nbsp;
                    <span style={{ display: 'inline-block', marginTop: "5px" }}>
                        <Button className="formButton positive" content="Search" compact
                            onClick={() => {
                                if (ctGovSearchState) {
                                    ctGovSearch(ctGovSearchState, searchRecruitingState, apiToken, dispatch, setCtGovSearchClicked, setCtGovSearchResultsState, setAlertModalState, setCtGovSearchRequestUrlState);
                                }
                            }} disabled={siteIsLoading || openAdvancedCTgovSearchState}
                        />
                        &nbsp;&nbsp;
                        {(ctGovSearchState || openAdvancedCTgovSearchState) &&
                            <Button className="formButton positive" content={"Save Search"} compact
                                onClick={async () => {
                                    setOpenSaveSearchModalState(true);
                                }}
                            />}
                    </span>
                    <br />
                    <div style={{ marginLeft: "16px", cursor: "pointer", width: "fit-content" }}
                        onClick={() => { setSearchRecruitingState(prevState => { return !prevState }); }}>
                        <FormControlLabel
                            className='unselectable'
                            style={{ pointerEvents: "none" }}
                            label="Only Studies that are Recruiting"
                            control={
                                <Checkbox
                                    style={{ pointerEvents: "auto" }}
                                    checked={searchRecruitingState}
                                    onChange={(e) => { setSearchRecruitingState(e.target.checked); }}
                                    color="primary"
                                    label="Only Studies that are Recruiting"
                                    name="Only Studies that are Recruiting"
                                    inputProps={{ 'aria-label': 'Only Studies that are Recruiting' }}
                                />
                            }
                        /></div>
                    <br />
                    {ctGovSearchResultsState?.length > 0 && <>
                        <h3>{ctGovSearchResultsState.length} results from ClinicalTrials.gov search.</h3>
                        <Button className="formButton positive"
                            content={"Save Search Results"} compact
                            onClick={() => { setSaveSearchResultsClickedState(true); }}
                        />
                        <br /><br />
                        {ctGovSearchResultsState.map((result, resultIndex) => {
                            return <p key={resultIndex}>
                                <a href={result.onClick} >{result.display}</a>
                            </p>
                        })}
                    </>}
                    {((ctGovSearchClicked) && ctGovSearchResultsState.length === 0) && <div>
                        <h3>No results from ClinicalTrials.gov search.</h3>
                    </div>}

                    <span className={"unselectable"} style={{ cursor: "pointer" }}
                        onClick={() => { setOpenAdvancedCTgovSearchState(!openAdvancedCTgovSearchState) }}>
                        Advanced ClinicalTrials.gov Search (CTgov API version) -&nbsp;
                        {openAdvancedCTgovSearchState ? <>Collapse ▼</> : <>Expand ►</>}</span>
                    {openAdvancedCTgovSearchState && <>
                        <br />
                        {/*After clicking I Should make it so the whole page turns grey, and if there are results it should bring you to the top of the page. */}
                        <Button className="formButton positive" content="Advanced Search" compact
                            onClick={() => {
                                advancedCtGovSearch(collectSearchParameters(), setCtGovSearchResultsState, setCtGovSearchClicked, setAlertModalState, apiToken, dispatch, setCtGovSearchRequestUrlState);
                            }} disabled={siteIsLoading} />
                        <br />
                        <TextField style={{ width: "70%", marginBottom: "8px" }} className="inputField" type='text' label={'Condition/disease'} size="small" variant='outlined' value={searchCondition} onChange={(e) => { setSearchCondition(e.target.value); }} />
                        <TextField style={{ width: "70%", marginBottom: "8px" }} className="inputField" type='text' label={'Other terms'} size="small" variant='outlined' value={searchOtherTerms} onChange={(e) => { setSearchOtherTerms(e.target.value); }} />
                        <TextField style={{ width: "70%", marginBottom: "8px" }} className="inputField" type='text' label={'Intervention/treatment'} size="small" variant='outlined' value={searchIntervention} onChange={(e) => { setSearchIntervention(e.target.value); }} />
                        <TextField style={{ width: "70%", marginBottom: "8px" }} className="inputField" type='text' label={'Location'} size="small" variant='outlined' value={searchLocation} onChange={(e) => { setSearchLocation(e.target.value); }} />
                        {/*titles (Title/acronym), outc (Outcome measure), spons (Sponsor/collaborator), lead (Lead sponsor name), patient (PatientSearch Area), id ("Study IDs" query in Essie expression syntax)*/}
                        <br />
                        <b>Study Status</b>
                        &nbsp;&nbsp;
                        <ToggleButtonGroup
                            className="yesNoRadioCheckBoxSwitch extrathin"
                            exclusive
                            value={searchStudyStatus || ""}
                            aria-label="Study Status"
                            name="Study Status"
                        >
                            <ToggleButton className="yesButton" value="all" aria-label="All studies" color="success" style={{ fontSize: "11px" }} onClick={() => { setSearchStudyStatus("all"); }}>

                                {searchStudyStatus === "all" ? <span className="box">☑&nbsp;</span> : <EmptyBoxCharacter />}

                                All studies
                            </ToggleButton>
                            <ToggleButton className="otherButton" value="recruitingAndNotYet" aria-label="Recruiting and not yet recruiting studies" color="secondary" style={{ fontSize: "11px" }} onClick={() => { setSearchStudyStatus("recruitingAndNotYet"); }}>
                                <span>
                                    {searchStudyStatus === "recruitingAndNotYet" ? <span className="box">☑&nbsp;</span> : <EmptyBoxCharacter />}
                                </span>
                                Recruiting and not yet recruiting studies&nbsp;
                            </ToggleButton>
                        </ToggleButtonGroup>
                        <br /><br />
                        <b>Study Results</b>
                        &nbsp;&nbsp;
                        <ToggleButtonGroup
                            className="genericToggleButtonGroup buttonGroupWrap extrathin"
                            exclusive
                            value={searchStudyResultsType}
                            aria-label="Study Results"
                            name="Study Results"
                        >
                            <ToggleButton className="genericToggleButton" color="primary"
                                aria-label="All studies" value="all"
                                onClick={() => { setSearchStudyResultsType("all"); }}>
                                <span>
                                    {searchStudyResultsType === "all" ? <span className="box">☑&nbsp;</span> : <EmptyBoxCharacter />}
                                </span>
                                All studies
                            </ToggleButton>
                            <ToggleButton className="genericToggleButton" color="primary"
                                aria-label="With results" value="with"
                                onClick={() => { setSearchStudyResultsType("with"); }}>
                                <span>
                                    {searchStudyResultsType === "with" ? <span className="box">☑&nbsp;</span> : <EmptyBoxCharacter />}
                                </span>
                                With results
                            </ToggleButton>
                            <ToggleButton className="genericToggleButton" color="primary"
                                aria-label="Without results" value="without"
                                onClick={() => { setSearchStudyResultsType("without"); }}>
                                <span>
                                    {searchStudyResultsType === "without" ? <span className="box">☑&nbsp;</span> : <EmptyBoxCharacter />}
                                </span>
                                Without results
                            </ToggleButton>
                        </ToggleButtonGroup>
                        <br />
                        <h4>Eligibility Criteria</h4>
                        <ToggleButtonGroup
                            className="genericToggleButtonGroup buttonGroupWrap extrathin"
                            exclusive
                            value={searchSex}
                            aria-label="Sex"
                            name="Sex"
                        //onClick={(e) => { handleClick(e.target.value) }}
                        >
                            <ToggleButton className="genericToggleButton" color="primary"
                                aria-label="All sexes" value="all"
                                onClick={() => { setSearchSex("all"); }}>
                                <span>
                                    {searchSex === "all" ? <span className="box">☑&nbsp;</span> : <EmptyBoxCharacter />}
                                </span>
                                All sexes
                            </ToggleButton>
                            <ToggleButton className="genericToggleButton" color="primary"
                                aria-label="Female" value="f"
                                onClick={() => { setSearchSex("f"); }}>
                                <span>
                                    {searchSex === "f" ? <span className="box">☑&nbsp;</span> : <EmptyBoxCharacter />}
                                </span>
                                Female
                            </ToggleButton>
                            <ToggleButton className="genericToggleButton" color="primary"
                                aria-label="Male" value="m"
                                onClick={() => { setSearchSex("m"); }}>
                                <span>
                                    {searchSex === "m" ? <span className="box">☑&nbsp;</span> : <EmptyBoxCharacter />}
                                </span>
                                Male
                            </ToggleButton>
                        </ToggleButtonGroup>
                        <br />
                        Age range criteria coming soon...
                        {/*TO DO: Age and age range*/}
                        <br />
                        <div className="unselectable" style={{ cursor: "pointer" }} onClick={() => { setSearchAcceptsHealthyVolunteers(prevState => { return !prevState; }); }}>
                            <b>Accepts healthy volunteers</b>&nbsp;<Checkbox
                                checked={searchAcceptsHealthyVolunteers}
                                color="primary"
                                style={{ paddingLeft: "4px", paddingRight: "4px" }}
                            />
                        </div>
                        <h4 style={{ marginTop: "6px", marginBottom: "0px" }}>Study Phase</h4>
                        <div style={{ paddingLeft: "16px" }}>
                            <div className="unselectable" style={{ cursor: "pointer" }} onClick={() => { setSearchPhase0(prevState => { return !prevState; }); }}><Checkbox style={{ padding: "0px 0px 4px 0px" }} checked={searchPhase0} size="small" color="primary" /> <span>Early Phase 1</span></div>
                            <div className="unselectable" style={{ cursor: "pointer" }} onClick={() => { setSearchPhase1(prevState => { return !prevState; }); }}><Checkbox style={{ padding: "0px 0px 4px 0px" }} checked={searchPhase1} size="small" color="primary" /> <span>Phase 1</span></div>
                            <div className="unselectable" style={{ cursor: "pointer" }} onClick={() => { setSearchPhase2(prevState => { return !prevState; }); }}><Checkbox style={{ padding: "0px 0px 4px 0px" }} checked={searchPhase2} size="small" color="primary" /> <span>Phase 2</span></div>
                            <div className="unselectable" style={{ cursor: "pointer" }} onClick={() => { setSearchPhase3(prevState => { return !prevState; }); }}><Checkbox style={{ padding: "0px 0px 4px 0px" }} checked={searchPhase3} size="small" color="primary" /> <span>Phase 3</span></div>
                            <div className="unselectable" style={{ cursor: "pointer" }} onClick={() => { setSearchPhase4(prevState => { return !prevState; }); }}><Checkbox style={{ padding: "0px 0px 4px 0px" }} checked={searchPhase4} size="small" color="primary" /> <span>Phase 4</span></div>
                            <div className="unselectable" style={{ cursor: "pointer" }} onClick={() => { setSearchPhaseNA(prevState => { return !prevState; }); }}><Checkbox style={{ padding: "0px 0px 4px 0px" }} checked={searchPhaseNA} size="small" color="primary" /> <span>Phase NA</span></div>
                        </div>
                        <h4 style={{ marginTop: "6px", marginBottom: "0px" }}>Study Type</h4>
                        <div style={{ paddingLeft: "16px" }}>
                            <div className="unselectable" style={{ cursor: "pointer" }}
                                onClick={() => { setSearchStudyTypeInt(prevState => { return !prevState; }); }}><Checkbox style={{ padding: "0px 0px 4px 0px" }} checked={searchStudyTypeInt} size="small" color="primary" /> <span>Interventional</span></div>
                            <div className="unselectable" style={{ cursor: "pointer" }}
                                onClick={() => { setSearchStudyTypeObs(prevState => { let checked = !prevState; if (!checked && searchStudyTypeObsPatReg) { setSearchStudyTypeObsPatReg(false); } return checked; }); }}><Checkbox style={{ padding: "0px 0px 4px 0px" }} checked={searchStudyTypeObs} size="small" color="primary" /> <span>Observational</span></div>
                            <div className="unselectable" style={{ cursor: "pointer", marginLeft: "16px" }}
                                onClick={() => { setSearchStudyTypeObsPatReg(prevState => { let checked = !prevState; if (checked && !searchStudyTypeObs) { setSearchStudyTypeObs(true); } return checked; }); }}><Checkbox style={{ padding: "0px 0px 4px 0px" }} checked={searchStudyTypeObsPatReg} size="small" color="primary" /> <span>Patient registries</span></div>
                            <div className="unselectable" style={{ cursor: "pointer" }}
                                onClick={() => {
                                    setSearchStudyTypeExp(prevState => {
                                        let checked = !prevState;
                                        if (!checked) {
                                            if (searchStudyTypeExpIndiv) { setSearchStudyTypeExpIndiv(false); }
                                            if (searchStudyTypeExpInter) { setSearchStudyTypeExpInter(false); }
                                            if (searchStudyTypeExpTreat) { setSearchStudyTypeExpTreat(false); }
                                        }
                                        return checked;
                                    });
                                }}>
                                <Checkbox style={{ padding: "0px 0px 4px 0px" }}
                                    checked={searchStudyTypeExp} size="small" color="primary" />
                                <span>Expanded Access</span></div>
                            <div className="unselectable" style={{ cursor: "pointer", marginLeft: "16px" }}
                                onClick={() => {
                                    setSearchStudyTypeExpIndiv(prevState => {
                                        let checked = !prevState;
                                        if (checked && !searchStudyTypeExp) { setSearchStudyTypeExp(true); }
                                        return checked;
                                    });
                                }}>
                                <Checkbox style={{ padding: "0px 0px 4px 0px" }}
                                    checked={searchStudyTypeExpIndiv} size="small" color="primary" />
                                <span>Individual patients</span></div>
                            <div className="unselectable" style={{ cursor: "pointer", marginLeft: "16px" }}
                                onClick={() => {
                                    setSearchStudyTypeExpInter(prevState => {
                                        let checked = !prevState;
                                        if (checked && !searchStudyTypeExp) { setSearchStudyTypeExp(true); }
                                        return checked;
                                    });
                                }}>
                                <Checkbox style={{ padding: "0px 0px 4px 0px" }}
                                    checked={searchStudyTypeExpInter} size="small" color="primary" />
                                <span>Intermediate-size population</span></div>
                            <div className="unselectable" style={{ cursor: "pointer", marginLeft: "16px" }}
                                onClick={() => {
                                    setSearchStudyTypeExpTreat(prevState => {
                                        let checked = !prevState;
                                        if (checked && !searchStudyTypeExp) { setSearchStudyTypeExp(true); }
                                        return checked;
                                    });
                                }}>
                                <Checkbox style={{ padding: "0px 0px 4px 0px" }}
                                    checked={searchStudyTypeExpTreat} size="small" color="primary" />
                                <span>Treatment IND/Protocol</span></div>
                        </div>
                        <h4 style={{ marginTop: "6px", marginBottom: "0px" }}>Study Documents</h4>
                        <div style={{ paddingLeft: "16px" }}>
                            <div className="unselectable" style={{ cursor: "pointer" }}
                                onClick={() => { setSearchDocumentsProtocols(prevState => { return !prevState; }); }}>
                                <Checkbox style={{ padding: "0px 0px 4px 0px" }}
                                    checked={searchDocumentsProtocols} size="small" color="primary" />
                                <span>Study protocols</span></div>
                            <div className="unselectable" style={{ cursor: "pointer" }}
                                onClick={() => { setSearchDocumentsSAPs(prevState => { return !prevState; }); }}>
                                <Checkbox style={{ padding: "0px 0px 4px 0px" }}
                                    checked={searchDocumentsSAPs} size="small" color="primary" />
                                <span>Statistical analysis plans (SAPs)</span></div>
                            <div className="unselectable" style={{ cursor: "pointer" }}
                                onClick={() => { setSearchDocumentsICFs(prevState => { return !prevState; }); }}>
                                <Checkbox style={{ padding: "0px 0px 4px 0px" }}
                                    checked={searchDocumentsICFs} size="small" color="primary" />
                                <span>Informed consent forms (ICFs)</span></div>
                        </div>
                        <h4 style={{ marginTop: "6px", marginBottom: "0px" }}>Funder Type</h4>
                        <div style={{ paddingLeft: "16px" }}>
                            <div className="unselectable" style={{ cursor: "pointer" }}
                                onClick={() => { setSearchFunderNIH(prevState => { return !prevState; }); }}>
                                <Checkbox style={{ padding: "0px 0px 4px 0px" }}
                                    checked={searchFunderNIH} size="small" color="primary" />
                                <span>NIH</span></div>
                            <div className="unselectable" style={{ cursor: "pointer" }}
                                onClick={() => { setSearchFunderFed(prevState => { return !prevState; }); }}>
                                <Checkbox style={{ padding: "0px 0px 4px 0px" }}
                                    checked={searchFunderFed} size="small" color="primary" />
                                <span>Other U.S. federal agency</span></div>
                            <div className="unselectable" style={{ cursor: "pointer" }}
                                onClick={() => { setSearchFunderIndustry(prevState => { return !prevState; }); }}>
                                <Checkbox style={{ padding: "0px 0px 4px 0px" }}
                                    checked={searchFunderIndustry} size="small" color="primary" />
                                <span>Industry</span></div>
                            <div className="unselectable" style={{ cursor: "pointer" }}
                                onClick={() => { setSearchFunderOther(prevState => { return !prevState; }); }}>
                                <Checkbox style={{ padding: "0px 0px 4px 0px" }}
                                    checked={searchFunderOther} size="small" color="primary" />
                                <span>All others (individuals, universities, organizations)</span></div>
                        </div>
                        <h4 style={{ marginTop: "6px", marginBottom: "0px" }}>Date Range</h4>
                        <div style={{ paddingLeft: "16px" }}>Date range search coming soon...</div>
                        <h4 style={{ marginTop: "6px", marginBottom: "0px" }}>More Ways to Search</h4>
                        <div style={{ paddingLeft: "16px" }}>
                            <TextField style={{ width: "70%", marginBottom: "8px" }}
                                className="inputField" type='text' label={'Title and/or Title Acronym'} size="small"
                                variant='outlined' value={searchTitles}
                                onChange={(e) => { setSearchTitles(e.target.value); }} />
                            <TextField style={{ width: "70%", marginBottom: "8px" }}
                                className="inputField" type='text' label={'Outcome measure'} size="small"
                                variant='outlined' value={searchOutcomeMeasure}
                                onChange={(e) => { setSearchOutcomeMeasure(e.target.value); }} />
                            <TextField style={{ width: "70%", marginBottom: "8px" }}
                                className="inputField" type='text' label={'Sponsor and/or Collaborator'} size="small"
                                variant='outlined' value={searchSponsor}
                                onChange={(e) => { setSearchSponsor(e.target.value); }} />
                            <TextField style={{ width: "70%", marginBottom: "8px" }}
                                className="inputField" type='text' label={'Sponsor (Lead)'} size="small"
                                variant='outlined' value={searchLeadSponsor}
                                onChange={(e) => { setSearchLeadSponsor(e.target.value); }} />
                            <TextField style={{ width: "70%", marginBottom: "8px" }}
                                className="inputField" type='text' label={'Study IDs'} size="small"
                                variant='outlined' value={searchStudyIds}
                                onChange={(e) => { setSearchStudyIds(e.target.value); }} /> {/*Do we need this field or can it be removed?*/}
                            <br />

                            <div className="unselectable" style={{ cursor: "pointer" }}
                                onClick={() => { setSearchFDAAAViolations(prevState => { return !prevState; }); }}>
                                <Checkbox style={{ padding: "0px 0px 4px 0px" }}
                                    checked={searchFDAAAViolations} size="small"
                                    color="primary" />
                                <span>Yes, has FDAAA 801 Violations</span>
                            </div>
                            {/*TO DO Facility Name, I'm not sure how this is different from Location in the API documentation.*/}
                        </div>
                        <br />
                        {/*After clicking I Should make it so the whole page turns grey, and if there are results it should bring you to the top of the page. */}
                        <Button className="formButton positive" content="Advanced Search" compact
                            onClick={() => {
                                advancedCtGovSearch(collectSearchParameters(), setCtGovSearchResultsState, setCtGovSearchClicked, setAlertModalState, apiToken, dispatch, setCtGovSearchRequestUrlState);
                            }} disabled={siteIsLoading} />
                    </>}
                </div>
            </Segment>
        </div>
    );
};

export default CTgovConvertPage;